import { FC, PropsWithChildren, useState } from 'react';

import { cva } from 'class-variance-authority';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import cn from 'utils/helpers/tailwindCn';

import NavAction from '../NavAction';

const perPageOptions = [
  {
    key: '6',
    text: '6 PER PAGE',
  },
  {
    key: '12',
    text: '12 PER PAGE',
  },
  {
    key: '24',
    text: '24 PER PAGE',
  },
];

const paginationWrapper = cva([
  'w-full',
  'bg-fill-tertiary',
  'flex flex-col items-center',
  'p-4',
  'relative min-h-[52px]',
]);

const pagination = cva([
  'flex space-x-10 items-center',
  'text-[12px] text-secondary-dark',
]);

const control = cva([
  'flex items-center space-x-10',
  'hover:text-title',
  'disabled:bg-transparent disabled:text-disabled',
  'disabled:hover:text-disabled',
]);

type PaginationProps = {
  page?: number;
  totalPages?: number;
  onClick?: (val: number) => void;
};

const Pagination: FC<PaginationProps> = ({
  page = 1,
  totalPages = 1,
  onClick,
}) => {
  const [currentPage, setCurrentPage] = useState(page);

  if (totalPages <= 1) return false;

  const pgs = [];

  for (let i = 1; totalPages >= i; i += 1) pgs.push(i);

  const handleMovePage = (type: 'next' | 'prev') => () => {
    let pg = currentPage;

    if (type === 'next') pg += 1;
    if (type === 'prev') pg -= 1;

    setCurrentPage(pg);
    onClick?.(pg);
  };

  const handleClick = (p: number) => () => {
    setCurrentPage(p);
    onClick?.(p);
  };

  return (
    <div className={cn(pagination())}>
      <button
        type="button"
        onClick={handleMovePage('prev')}
        className={cn(control())}
        disabled={currentPage <= 1}
      >
        <BiChevronLeft />
        <span>PREV</span>
      </button>
      <div
        className={cn('flex', 'gap-x-2.5', 'max-w-[500px]', 'overflow-x-auto')}
      >
        {pgs.map((pg) => (
          <button
            key={Math.random()}
            type="button"
            className={cn(
              currentPage === pg
                ? 'text-primary-base font-semibold'
                : 'text-content',
              'text-xs px-1',
              'hover:text-secondary-dark',
            )}
            onClick={handleClick(pg)}
          >
            {pg}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={handleMovePage('next')}
        className={cn(control())}
        disabled={currentPage >= totalPages}
      >
        <span>NEXT</span>
        <BiChevronRight />
      </button>
    </div>
  );
};

export default Pagination;

type PageActionProps = {
  onChangePerPage: (page: number) => void;
  perPage: number;
};

const PageAction: FC<PageActionProps> = ({ onChangePerPage, perPage }) => (
  <NavAction
    showArrow
    className="option-top-right md:absolute md:top-[35%] md:right-4 text-xs"
    label={`${perPage} PER PAGE`}
    onClick={(opt) => onChangePerPage(parseInt(opt.key, 10))}
    options={perPageOptions}
    optionsClassName="h-[130px]"
  />
);

type PaginationWrapperProps = PropsWithChildren<{
  hasActions?: boolean;
  total?: number;
  handlePerPage: (page: number) => void;
  perPage?: number;
}>;

export const PaginationWrapper: FC<PaginationWrapperProps> = ({
  handlePerPage,
  hasActions = false,
  children,
  perPage = 6,
  total,
}) => (
  <div className={cn(paginationWrapper())}>
    {children}
    {total ? (
      <div className="tracking-[0.06em] text-right w-full text-sm">
        ({total}) items
      </div>
    ) : (
      hasActions && (
        <PageAction onChangePerPage={handlePerPage} perPage={perPage} />
      )
    )}
  </div>
);
